import { createContext, use, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router'

import { useApiClient } from '@/shared/api/use-api-client'
import { useSession } from '@/shared/auth/session'
import { useFeatureFlag } from '@/shared/feature-flags'
import { LinkAccessProvider, useLinkAccess, type LinkAccess } from '@/shared/registry'

import { skillTabs } from './skill-tabs'
import { parseSkillsVisibility, skillsTrackingFlag, skillsVisibilityApi } from './skills'

/** How long an answer stands before the viewer's next page or return to the tab asks again. */
export const skillsVisibilityRefreshMilliseconds = 60_000

const SkillsVisibleContext = createContext(false)

/** Whether skills show to the viewer: the skills flag is on and their organization tracks skills. */
export function useSkillsVisible(): boolean {
  return use(SkillsVisibleContext)
}

/**
 * Says, app-wide, whether skills show to the viewer, and leaves Credentials' Skills tabs out of
 * every details page while they do not. Only with the flag on does it ask the API about the
 * viewer's organization: for each signed-in caller, then again on the next page or return to the
 * tab once a minute has passed, so a change to the setting shows without a reload. It never asks
 * on a timer, which would keep an idle session alive.
 */
export function SkillsProvider(props: { readonly children: ReactNode }) {
  const client = useApiClient()
  const isFlagOn = useFeatureFlag(skillsTrackingFlag)
  const caller = useSession().session?.userProfileId ?? null
  const { pathname } = useLocation()
  const [answer, setAnswer] = useState<{ readonly caller: string; readonly isVisible: boolean } | null>(null)
  // Asks again when the last answer is a minute old; nothing while the flag is off.
  const askIfStale = useRef<() => void>(() => undefined)

  useEffect(() => {
    if (!isFlagOn || caller === null) {
      askIfStale.current = () => undefined
      return undefined
    }
    const controller = new AbortController()
    // One request at a time, so a slow answer never overwrites a newer one.
    let isAsking = false
    let askedAt = 0
    const ask = async () => {
      if (isAsking) {
        return
      }
      isAsking = true
      askedAt = Date.now()
      try {
        const isVisible = parseSkillsVisibility(await client.get(skillsVisibilityApi, controller.signal))
        setAnswer({ caller, isVisible })
      } catch {
        // Hidden until a later ask answers.
        if (!controller.signal.aborted) {
          setAnswer({ caller, isVisible: false })
        }
      } finally {
        isAsking = false
      }
    }
    askIfStale.current = () => {
      if (Date.now() - askedAt >= skillsVisibilityRefreshMilliseconds) {
        void ask()
      }
    }
    void ask()
    const onFocus = () => askIfStale.current()
    window.addEventListener('focus', onFocus)
    return () => {
      controller.abort()
      window.removeEventListener('focus', onFocus)
      askIfStale.current = () => undefined
    }
  }, [client, isFlagOn, caller])

  // A new page is the viewer at work, so it may ask again.
  const shownPath = useRef(pathname)
  useEffect(() => {
    if (shownPath.current !== pathname) {
      shownPath.current = pathname
      askIfStale.current()
    }
  }, [pathname])

  const isVisible = isFlagOn && answer !== null && answer.caller === caller && answer.isVisible
  const outer = useLinkAccess()
  const canSee = useCallback<LinkAccess>(
    (entry, context) => {
      if (!isVisible && 'component' in entry && skillTabs.some((tab) => tab.component === entry.component)) {
        return false
      }
      return outer(entry, context)
    },
    [outer, isVisible],
  )

  return (
    <LinkAccessProvider value={canSee}>
      <SkillsVisibleContext value={isVisible}>{props.children}</SkillsVisibleContext>
    </LinkAccessProvider>
  )
}
